const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function getToken() {
  return localStorage.getItem('admin_token');
}

function clearToken() {
  localStorage.removeItem('admin_token');
}

async function adminFetch(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    clearToken();
    window.location.href = '/admin/login';
    throw new Error('Oturum süresi dolmuş. Yönlendiriliyor...');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Sunucu hatası: ${res.status}`);
  }

  const text = await res.text();
  return text ? JSON.parse(text) : {};
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function adminLogin(email, password) {
  const res = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || 'Giriş başarısız.');
  }
  return res.json();
}

export async function adminLogout() {
  return adminFetch('/api/admin/logout', { method: 'POST' });
}

// ── Media upload ──────────────────────────────────────────────────────────────

export async function uploadMedia(file, folder = 'projects') {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', folder);

  // Do NOT set Content-Type — browser sets it automatically with multipart boundary
  const res = await fetch(`${API_BASE}/api/admin/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (res.status === 401) {
    clearToken();
    window.location.href = '/admin/login';
    throw new Error('Oturum süresi dolmuş. Yönlendiriliyor…');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Yükleme hatası: ${res.status}`);
  }
  return res.json(); // { url, path }
}

// ── Projects ──────────────────────────────────────────────────────────────────

export async function listProjects() {
  return adminFetch('/api/admin/projects');
}

export async function getProject(id) {
  return adminFetch(`/api/admin/projects/${id}`);
}

export async function createProject(data) {
  return adminFetch('/api/admin/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProject(id, data) {
  return adminFetch(`/api/admin/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteProject(id) {
  return adminFetch(`/api/admin/projects/${id}`, { method: 'DELETE' });
}

export async function setDisplayOrder(id, value) {
  return adminFetch(`/api/admin/projects/${id}/display-order`, {
    method: 'PATCH',
    body: JSON.stringify({ value }),
  });
}

export async function togglePublished(id) {
  return adminFetch(`/api/admin/projects/${id}/toggle-published`, { method: 'PATCH' });
}

export async function updateProjectChildren(id, data) {
  return adminFetch(`/api/admin/projects/${id}/children`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ── Services ──────────────────────────────────────────────────────────────────

export async function listServices() {
  return adminFetch('/api/admin/services');
}

export async function createService(data) {
  return adminFetch('/api/admin/services', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateService(id, data) {
  return adminFetch(`/api/admin/services/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteService(id) {
  return adminFetch(`/api/admin/services/${id}`, { method: 'DELETE' });
}

// ── Company Info ──────────────────────────────────────────────────────────────

export async function getCompanyInfo() {
  return adminFetch('/api/admin/company-info');
}

export async function updateCompanyInfo(data) {
  return adminFetch('/api/admin/company-info', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ── Notification counts ───────────────────────────────────────────────────────

export async function getUnreadCounts() {
  return adminFetch('/api/admin/unread-counts');
}

// ── Favicon upload ────────────────────────────────────────────────────────────

export async function uploadFavicon(file) {
  const token = localStorage.getItem('admin_token');
  const formData = new FormData();
  formData.append('file', file);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const res = await fetch(`${API_BASE}/api/admin/upload-favicon`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem('admin_token');
    window.location.href = '/admin/login';
    throw new Error('Oturum süresi dolmuş.');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.detail || `Favicon yükleme hatası: ${res.status}`);
  }
  return res.json(); // { url, path }
}

// ── Messages ──────────────────────────────────────────────────────────────────

export async function listMessages() {
  return adminFetch('/api/admin/messages');
}

export async function markMessageRead(id) {
  return adminFetch(`/api/admin/messages/${id}/read`, { method: 'PATCH' });
}

export async function deleteMessage(id) {
  return adminFetch(`/api/admin/messages/${id}`, { method: 'DELETE' });
}

// ── Dream Requests ────────────────────────────────────────────────────────────

export async function listDreamRequests() {
  return adminFetch('/api/admin/dream-requests');
}

export async function markDreamRead(id) {
  return adminFetch(`/api/admin/dream-requests/${id}/read`, { method: 'PATCH' });
}

export async function deleteDreamRequest(id) {
  return adminFetch(`/api/admin/dream-requests/${id}`, { method: 'DELETE' });
}
